import { isProjectIntegrationDefined } from "@services/integrations-service/autodesk/integrations-service-utils";
import { BaseService } from "@services/integrations-service/base-service";
import { isAutodeskLegacyProjectIntegration } from "@services/integrations-service/integrations-type-guards";
import { HttpUtils } from "@stellar/utils";

/** Service that provides methods to manage the Autodesk 3rd party integration */
export class AutodeskService extends BaseService {
  integrationId = "autodesk";

  /**
   * @returns the IntegrationCompany entities of the user
   * @throws {Error} if it fails to get the entities
   */
  async getIntegrationCompanies() {
    const proxyRequest = this.coreApiClient.integration.getAutodeskHubs;
    const {
      data
    } = await this.request(proxyRequest);
    return data.map(autodeskHub => ({
      integrationId: "autodesk",
      id: autodeskHub.id,
      name: autodeskHub.attributes.name
    }));
  }

  /**
   * @returns the IntegrationProject entities for a given integration company
   * @throws {Error} if it fails to get the entities
   * @param companyId ID of the company
   */
  async getIntegrationProjects(companyId) {
    const proxyRequest = () => this.coreApiClient.integration.getAutodeskProjects(companyId);
    const {
      data
    } = await this.request(proxyRequest);
    // Keep only projects that have both issues and RFIs entities defined
    const projects = data.filter(this.isAutodeskProjectWithIssuesAndRfis);
    const integrationProjects = projects.map(project => {
      const issuesBcfProjectId = this.getIssuesBcfProjectId(project);
      const rfisBcfProjectId = this.getRfisBcfProjectId(project);
      return {
        integrationId: "autodesk",
        id: project.id,
        name: project.attributes.name,
        companyId,
        rfisBcfProjectId,
        issuesOrObservationsBcfProjectId: issuesBcfProjectId,
        type: project.attributes.extension.data.projectType
      };
    });
    return integrationProjects;
  }

  /**
   * Handles errors originating from the CoreAPI proxy backend for requests issued to the Autodesk API.
   * - If the error status is UNAUTHORIZED, FORBIDDEN or CONFLICT it attempts to update the token.
   * @param error Error to handle
   * @throws {HttpUtils.FetchError} if it can't handle the error.
   */
  async handleIntegrationError(error) {
    switch (error.status) {
      case HttpUtils.HTTP_ERROR_CODE.Unauthorized:
      case HttpUtils.HTTP_ERROR_CODE.Forbidden:
      case HttpUtils.HTTP_ERROR_CODE.Conflict:
        await this.handleTokenUpdate();
        break;
      default:
        throw error;
    }
  }

  /**
   * Updates the project connection data to the Sphere XG format.
   * @returns The project integrations map with the updated data format
   * @param data Project integrations map
   * @throws {Error} if it fails to fetch the project entity from the Autodesk API or if the
   * fetched project doesn't have issues or RFIs data.
   */
  async updateDataFormat(data) {
    const proxyRequest = () => this.coreApiClient.integration.getAutodeskProject(data.autodesk.hubId, data.autodesk.projectId);
    const {
      data: project
    } = await this.request(proxyRequest);
    if (!this.isAutodeskProjectWithIssuesAndRfis(project)) {
      throw new Error("Autodesk project doesn't have issues or RFIs data");
    }
    const projectType = project.attributes.extension.data.projectType;
    const issuesBcfProjectId = this.getIssuesBcfProjectId(project);
    const rfisBcfProjectId = this.getRfisBcfProjectId(project);
    const accData = {
      ...data,
      ["autodesk-acc.issues"]: {
        bcfProjectId: issuesBcfProjectId
      },
      ["autodesk-acc.rfis"]: {
        bcfProjectId: rfisBcfProjectId
      }
    };
    const bim360Data = {
      ...data,
      ["autodesk-bim360.issues"]: {
        bcfProjectId: issuesBcfProjectId
      },
      ["autodesk-bim360.rfis"]: {
        bcfProjectId: rfisBcfProjectId
      }
    };
    return projectType === "ACC" ? accData : bim360Data;
  }

  /**
   * @returns Whether there is a project connected in the legacy (HB) data format.
   * @param data Project integrations map
   */
  isConnectedInLegacyFormat(data) {
    return isAutodeskLegacyProjectIntegration(data.autodesk);
  }

  /**
   * @returns Whether there is a project connected in the XG data format.
   * @param projectIntegrationsMap Project integrations map
   */
  isConnectedInXGFormat(projectIntegrationsMap) {
    return this.isAccConnectedInXGFormat(projectIntegrationsMap) || this.isBim360ConnectedInXGFormat(projectIntegrationsMap);
  }

  /**
   * @returns Whether there is a project connected either in legacy or XG data format.
   * @param projectIntegrationsMap Project integrations map
   */
  isConnected(projectIntegrationsMap) {
    return this.isConnectedInLegacyFormat(projectIntegrationsMap) || this.isConnectedInXGFormat(projectIntegrationsMap);
  }

  /**
   * @returns The bcfProjectId for Autodesk issues.
   * @param project Project entity
   */
  getIssuesBcfProjectId(project) {
    const containerId = project.relationships.issues.data.id;
    const hubId = project.relationships.hub.data.id;
    return `${containerId}~${project.id}~${hubId}`;
  }

  /**
   * @returns The bcfProjectId for Autodesk RFIs.
   * @param project Project entity
   */
  getRfisBcfProjectId(project) {
    const containerId = project.relationships.rfis.data.id;
    const hubId = project.relationships.hub.data.id;
    return `${containerId}~${project.id}~${hubId}`;
  }

  /**
   * @returns whether the passed project has both issues and RFIs entities defined
   * @param project Autodesk project entity
   */
  isAutodeskProjectWithIssuesAndRfis(project) {
    return project.relationships.issues !== undefined && project.relationships.rfis !== undefined;
  }

  /**
   * @returns Whether there is an ACC projected connected in the XG data format.
   * @param projectIntegrationsMap Project integrations map
   */
  isAccConnectedInXGFormat(projectIntegrationsMap) {
    const isAutodeskAccIssuesConnected = isProjectIntegrationDefined(projectIntegrationsMap["autodesk-acc.issues"]);
    const isAutodeskAccRfisConnected = isProjectIntegrationDefined(projectIntegrationsMap["autodesk-acc.rfis"]);
    return isAutodeskAccIssuesConnected && isAutodeskAccRfisConnected;
  }

  /**
   * @returns Whether there is a Bim360 projected connected in the XG data format.
   * @param projectIntegrationsMap Project integrations map
   */
  isBim360ConnectedInXGFormat(projectIntegrationsMap) {
    const isAutodeskBim360IssuesConnected = isProjectIntegrationDefined(projectIntegrationsMap["autodesk-bim360.issues"]);
    const isAutodeskBim360RfisConnected = isProjectIntegrationDefined(projectIntegrationsMap["autodesk-bim360.rfis"]);
    return isAutodeskBim360IssuesConnected && isAutodeskBim360RfisConnected;
  }
}